import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { landingPathFor } from "@/lib/landingPath";
import { BillingSettingsProvider } from "@/contexts/BillingSettingsContext";
import { PlanFlagsProvider } from "@/contexts/PlanFlagsContext";
import { AddonsProvider } from "@/contexts/AddonsContext";
import { Toaster } from "@/components/ui/sonner";
import { ROUTE_ROLES } from "@/lib/routeRoles";

import AppShell from "@/components/AppShell";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import SignupPage from "@/pages/SignupPage";
import PricingPage from "@/pages/PricingPage";
import DashboardPage from "@/pages/DashboardPage";
import AttendancePage from "@/pages/AttendancePage";
import QRDisplayPage from "@/pages/qr/QRDisplayPage";
import QRScanPage from "@/pages/qr/QRScanPage";
import QueuePage from "@/pages/QueuePage";
import TherapistQueuePage from "@/pages/TherapistQueuePage";
import StudentsPage from "@/pages/StudentsPage";
import StudentDetailPage from "@/pages/StudentDetailPage";
import SessionsListPage from "@/pages/SessionsListPage";
import SessionDetailPage from "@/pages/SessionDetailPage";
import { BillingListPage, InvoiceDetailPage } from "@/pages/BillingPage";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/PaymentPages";
import ReportsPage from "@/pages/ReportsPage";
import MessagesPage from "@/pages/MessagesPage";
import AuditLogPage from "@/pages/AuditLogPage";
import SettingsPage from "@/pages/SettingsPage";
import InventoryPage from "@/pages/InventoryPage";
import PayrollPage from "@/pages/PayrollPage";
import PayrollReviewPage from "@/pages/PayrollReviewPage";
import MyPayslipsPage from "@/pages/MyPayslipsPage";
import MyApprovalsPage from "@/pages/MyApprovalsPage";
import EmployeesPage from "@/pages/workforce/EmployeesPage";
import ScheduleView from "@/pages/workforce/ScheduleView";
import HolidaysPage from "@/pages/workforce/HolidaysPage";
import SalaryRulesPage from "@/pages/workforce/SalaryRulesPage";
import CertificationsPage from "@/pages/CertificationsPage";
import GrantsPage from "@/pages/GrantsPage";
import PlatformAdminPage from "@/pages/PlatformAdminPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import AppointmentsPage from "@/pages/AppointmentsPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ConfirmEmailPage from "@/pages/ConfirmEmailPage";
import IntakePage from "@/pages/IntakePage";
import SuperAdminLoginPage from "@/pages/SuperAdminLoginPage";
import QRCheckinPage from "@/pages/QRCheckinPage";
import PublishFeedPage from "@/pages/publish/PublishFeedPage";
import PostDetailPage from "@/pages/publish/PostDetailPage";

import "@/App.css";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

/** Redirects to /dashboard when the caller's active role isn't allowed on `path`.
 *  Backend endpoints still enforce role checks; this is UX defense-in-depth so
 *  users don't see a broken admin page after typing a URL. Single source of
 *  truth lives in `/app/frontend/src/lib/routeRoles.js`, shared with AppShell's
 *  sidebar NAV_GROUPS table.
 */
function RoleGate({ path, children }) {
  const { activeRole, user } = useAuth();
  if (!activeRole) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  const allow = ROUTE_ROLES[path] || [];
  if (!allow.includes(activeRole)) return <Navigate to={landingPathFor(user)} replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user && user !== false) {
    // Respect ?next=<same-origin path> so QR deep links survive an
    // already-logged-in session.
    const next = new URLSearchParams(window.location.search).get("next");
    if (next && next.startsWith("/") && !next.startsWith("//")) {
      return <Navigate to={next} replace />;
    }
    return <Navigate to={landingPathFor(user)} replace />;
  }
  return children;
}

/** Gate a route to super_admin only. Non-super-admin users hitting this
 *  route (including by typing the URL) get bounced to their own landing
 *  page — the /platform route becomes strictly unreachable to normal
 *  users, satisfying the "must not be reachable, not even by direct URL"
 *  bug report from 28-Jul-2026. */
function SuperAdminGate({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.platform_role !== "super_admin") return <Navigate to={landingPathFor(user)} replace />;
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  return <Navigate to={landingPathFor(user)} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <BrandingProvider>
        <AuthProvider>
          <BillingSettingsProvider>
            <PlanFlagsProvider>
              <AddonsProvider>
        <OAuthGate />
        <Routes>
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
          <Route path="/t/:slug/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
          <Route path="/signup" element={<PublicOnly><SignupPage /></PublicOnly>} />
          <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/confirm-email" element={<ConfirmEmailPage />} />
          <Route path="/intake/:token" element={<IntakePage />} />
          <Route path="/qr/checkin" element={<QRCheckinPage />} />
          {/* iter-56 — Publish public surfaces (no auth). Must be
              registered BEFORE the AppShell block so they don't get
              wrapped in the Protected gate. */}
          <Route path="/publish" element={<PublishFeedPage isPublic />} />
          <Route path="/publish/:id" element={<PostDetailPage isPublic />} />
          <Route path="/sa/login" element={<PublicOnly><SuperAdminLoginPage /></PublicOnly>} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
          <Route element={<Protected><AppShell /></Protected>}>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/attendance" element={<RoleGate path="/attendance"><AttendancePage /></RoleGate>} />
            <Route path="/qr/display" element={<RoleGate path="/qr/display"><QRDisplayPage /></RoleGate>} />
            <Route path="/qr/scan" element={<RoleGate path="/qr/scan"><QRScanPage /></RoleGate>} />
            <Route path="/queue" element={<RoleGate path="/queue"><QueuePage /></RoleGate>} />
            <Route path="/therapists" element={<RoleGate path="/therapists"><TherapistQueuePage /></RoleGate>} />
            <Route path="/sessions" element={<RoleGate path="/sessions"><SessionsListPage /></RoleGate>} />
            <Route path="/sessions/:id" element={<RoleGate path="/sessions"><SessionDetailPage /></RoleGate>} />
            <Route path="/students" element={<RoleGate path="/students"><StudentsPage /></RoleGate>} />
            <Route path="/students/:id" element={<RoleGate path="/students"><StudentDetailPage /></RoleGate>} />
            <Route path="/billing" element={<RoleGate path="/billing"><BillingListPage /></RoleGate>} />
            <Route path="/billing/:id" element={<RoleGate path="/billing"><InvoiceDetailPage /></RoleGate>} />
            <Route path="/reports" element={<RoleGate path="/reports"><ReportsPage /></RoleGate>} />
            <Route path="/messages" element={<RoleGate path="/messages"><MessagesPage /></RoleGate>} />
            <Route path="/audit" element={<RoleGate path="/audit"><AuditLogPage /></RoleGate>} />
            <Route path="/settings" element={<RoleGate path="/settings"><SettingsPage /></RoleGate>} />
            <Route path="/inventory" element={<RoleGate path="/inventory"><InventoryPage /></RoleGate>} />
            <Route path="/payroll" element={<RoleGate path="/payroll"><PayrollPage /></RoleGate>} />
            <Route path="/payroll/runs/:id" element={<RoleGate path="/payroll"><PayrollReviewPage /></RoleGate>} />
            <Route path="/my-payslips" element={<MyPayslipsPage />} />
            <Route path="/my-approvals" element={<MyApprovalsPage />} />
            <Route path="/certifications" element={<RoleGate path="/certifications"><CertificationsPage /></RoleGate>} />
            <Route path="/grants" element={<RoleGate path="/grants"><GrantsPage /></RoleGate>} />
            <Route path="/appointments" element={<RoleGate path="/appointments"><AppointmentsPage /></RoleGate>} />
            {/* iter-56 — In-app Publish surfaces. Composer + tabs live here. */}
            <Route path="/app/publish" element={<PublishFeedPage />} />
            <Route path="/app/publish/:id" element={<PostDetailPage />} />
            <Route path="/platform" element={<SuperAdminGate><PlatformAdminPage /></SuperAdminGate>} />
            <Route path="/billing/subscription" element={<SubscriptionPage />} />
            <Route path="/workforce/employees" element={<RoleGate path="/workforce/employees"><EmployeesPage /></RoleGate>} />
            <Route path="/workforce/schedule"  element={<RoleGate path="/workforce/schedule"><ScheduleView /></RoleGate>} />
            <Route path="/workforce/holidays"  element={<RoleGate path="/workforce/holidays"><HolidaysPage /></RoleGate>} />
            <Route path="/workforce/salary-rules" element={<RoleGate path="/workforce/salary-rules"><SalaryRulesPage /></RoleGate>} />
          </Route>
          <Route path="*" element={<RootRedirect />} />
        </Routes>
        <Toaster richColors position="top-right" />
              </AddonsProvider>
            </PlanFlagsProvider>
          </BillingSettingsProvider>
        </AuthProvider>
      </BrandingProvider>
    </BrowserRouter>
  );
}

// If the OAuth provider redirects us to any random route with #session_id=…,
// short-circuit to /auth/callback (synchronous, before ProtectedRoute renders).
function OAuthGate() {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash || "";
  if (hash.includes("session_id=") && window.location.pathname !== "/auth/callback") {
    window.history.replaceState({}, "", "/auth/callback" + hash);
  }
  return null;
}
